/*
 * Copyright (C) 2025 SimpliCity Digital Inc - All Rights Reserved
 */

import { isArray, isArrayNotEmpty } from "cms/utils/empty-exists";

/**
 * filterHidden
 * @template {NavItemLike} T
 * @param {T[]=} list
 */
const filterHidden = function (list) {
    const filtered = list?.filter((item) => !item.hide_in_nav);
    return filtered;
};

/**
 * filterHiddenRecursively
 * @template {NavItemLike} T
 * @param {object} webpage
 * @param {T[]} webpage.children
 */
const filterHiddenRecursively = (webpage) => {
    // filter object directly
    webpage.children = filterHidden(webpage.children);

    // filter children
    for (const child of webpage.children) {
        if (isArray(child.children)) {
            filterHiddenRecursively(child);
        }
    }
};

/**
 * readNode
 * @param webpage
 * @returns {Promise<{title: *, url: *, children: *, hide_in_nav: *}>}
 */
const readNode = async function (webpage) {
    if (!webpage) {
        return webpage;
    }
    const title = webpage?.nav_title;
    const children = webpage?.children;
    const url = webpage?.path;
    const hide_in_nav = webpage?.hide_in_nav;
    return { title, url, children, hide_in_nav };
};

/**
 * readChildren
 * @param webpage
 * @param level
 * @param side
 * @returns {Promise<*[]>}
 */
const readChildren = async function (webpage, level, side) {
    if (!webpage) {
        return webpage;
    }
    const all_children = webpage?.children;
    const filteredChildren = filterHidden(all_children);
    const children = [];
    if (isArrayNotEmpty(filteredChildren)) {
        for (const child of filteredChildren) {
            const child_data = await readNode(child);
            child_data.side = side;
            child_data.level = level + 1;
            if (child.children) {
                child_data.children = await readChildren(
                    child,
                    child_data?.level,
                    child_data?.side
                );
            }
            children.push(child_data);
        }
    }
    // returns only filtered children
    return children;
};

export {
    readNode,
    readChildren,
    filterHidden,
    filterHiddenRecursively
};


/**
 * @typedef {object} NavItemLike
 * @property {boolean | number} hide_in_nav
 */
